import { Box, Skeleton, Stack, Typography, styled } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";

function fmtVolume(n) {
  if (n >= 1e12) return `₦${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `₦${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `₦${(n / 1e6).toFixed(1)}M`;
  return `₦${n.toLocaleString()}`;
}

function srColor(sr) {
  if (sr === null) return { text: "text.disabled", bar: "#ccc" };
  if (sr >= 96) return { text: "#15803d", bar: "#22c55e" };
  if (sr >= 93) return { text: "#d97706", bar: "#f59e0b" };
  return { text: "#dc2626", bar: "#f87171" };
}

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const StatBox = styled(Box)(({ theme }) => ({
  flex: 1,
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.04)"
      : theme.palette.grey[50],
  borderRadius: 8,
  padding: theme.spacing(1.5),
}));

const StatusPill = styled(Box)(({ status }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  borderRadius: 999,
  fontSize: "0.7rem",
  fontWeight: 500,
  padding: "3px 10px",
  backgroundColor: status === "active" ? "#f0fdf4" : "#fef2f2",
  color: status === "active" ? "#15803d" : "#dc2626",
}));

const StatusDot = styled("span")(({ status }) => ({
  width: 6,
  height: 6,
  borderRadius: "50%",
  backgroundColor: status === "active" ? "#15803d" : "#dc2626",
}));

const SuccessBar = styled(Box)({
  flex: 1,
  height: 4,
  borderRadius: 2,
  overflow: "hidden",
  backgroundColor: "rgba(0,0,0,0.08)",
});

const ViewButton = styled("button")(({ theme }) => ({
  width: "100%",
  background: "transparent",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  padding: "7px 12px",
  fontSize: "0.8rem",
  color: theme.palette.text.primary,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  "&:hover": { backgroundColor: theme.palette.action.hover },
}));

const ProductCard = ({ product }) => {
  const navigate = useNavigate();

  const inactive = product.status === "inactive";
  const vol = product.stats.volume;
  const cnt = product.stats.count;
  const sr = product.stats.success_rate;
  const { text: srText, bar: srBar } = srColor(sr);

  return (
    <Card>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography fontWeight={600} fontSize="0.95rem">
            {product.name}
          </Typography>
        </Box>

        <StatusPill status={product.status}>
          <StatusDot status={product.status} />
          {product.status}
        </StatusPill>
      </Box>

      <Stack direction="row" spacing={1}>
        <StatBox>
          <Typography variant="caption" color="text.secondary">
            Volume
          </Typography>
          <Typography fontWeight={600} fontSize="0.9rem">
            {inactive ? "—" : fmtVolume(vol)}
          </Typography>
        </StatBox>

        <StatBox>
          <Typography variant="caption" color="text.secondary">
            Transactions
          </Typography>
          <Typography fontWeight={600} fontSize="0.9rem">
            {inactive ? "—" : cnt.toLocaleString()}
          </Typography>
        </StatBox>
      </Stack>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Success rate
        </Typography>

        {sr !== null ? (
          <>
            <SuccessBar>
              <Box
                sx={{
                  width: `${sr}%`,
                  height: "100%",
                  backgroundColor: srBar,
                }}
              />
            </SuccessBar>

            <Typography
              variant="caption"
              fontWeight={500}
              sx={{ color: srText, minWidth: 38 }}
            >
              {sr}%
            </Typography>
          </>
        ) : (
          <Typography variant="caption" color="text.disabled">
            N/A
          </Typography>
        )}
      </Box>

      <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1.5 }}>
        <ViewButton onClick={() => navigate(`/products/overview/${product.id}`)}>
          <span>View &amp; configure</span>
          <ArrowForwardIcon sx={{ fontSize: 16 }} />
        </ViewButton>
      </Box>
    </Card>
  );
};

export const ProductCardSkeleton = () => (
  <Card>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <Skeleton variant="text" width={100} height={24} />
      <Skeleton variant="rounded" width={64} height={20} sx={{ borderRadius: 999 }} />
    </Box>

    <Stack direction="row" spacing={1}>
      <StatBox>
        <Skeleton variant="text" width={40} height={16} />
        <Skeleton variant="text" width={70} height={22} />
      </StatBox>

      <StatBox>
        <Skeleton variant="text" width={70} height={16} />
        <Skeleton variant="text" width={50} height={22} />
      </StatBox>
    </Stack>

    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Skeleton variant="text" width={80} height={16} />
      <Skeleton variant="rounded" height={4} sx={{ flex: 1, borderRadius: 2 }} />
      <Skeleton variant="text" width={30} height={16} />
    </Box>

    <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1.5 }}>
      <Skeleton variant="rounded" height={34} sx={{ borderRadius: 1 }} />
    </Box>
  </Card>
);

export default ProductCard;
